import { Directive, ViewChild, Input } from '@angular/core';
import { DynamicTypeaheadBaseComponent } from '@ups/xplat/dynamic-render';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import {
  bindTypeaheadViewToControl,
  passMultiSelectValueChange,
  typeaheadPickOrNull,
  TypeaheadViewStatus,
  typeaheadViewControl,
  typeaheadViewItem,
  typeaheadViewStatus,
} from './dynamic-typeahead-view-binding';

type SelectedItem = Parameters<
  DynamicTypeaheadBaseComponent['valueChangeItem']
>[0];

@Directive()
export abstract class DynamicTypeaheadWebBaseComponent extends DynamicTypeaheadBaseComponent {
  @ViewChild('dropdown') dropdown: DropDownListComponent;
  /** Tabindex to propagate to the underlying kendo dropdown */
  @Input({ transform: parseInt }) tabindex: number;

  setValue(value: unknown) {
    if (this.dropdown) {
      this.dropdown.value = value;
    }
  }

  /**
   * For templates that bind the kendo control with `[value]`/`(valueChange)` instead of
   * `[formGroup]`/`[formControlName]` (pre-commit 13d: no new reactive-form bindings). A user pick
   * writes the form control here, which `[formControlName]` used to do, then runs the shared
   * selection logic (fan-out, conflict provenance) exactly as before.
   */
  pickFromView(item: SelectedItem) {
    const pick = typeaheadPickOrNull(item, this.config?.placeholder);
    this.updateValue(pick);
    this.valueChangeItem(pick);
  }

  /** A kendo-multiselect's (valueChange) hands its whole selection (see passMultiSelectValueChange). */
  multiSelectValueChange(items: SelectedItem[]) {
    passMultiSelectValueChange(this, items);
  }

  /** Multi-select counterpart of `pickFromView`. */
  pickManyFromView(items: Array<SelectedItem>) {
    this.updateValue(items);
    this.valueChangeItems(items);
  }

  /** Marks the form control touched on blur (as `[formControlName]` did), so required errors show. */
  blurFromView() {
    this.viewControl()?.markAsTouched();
    this.onBlur();
  }

  /** Task 35874: the object-only value for a `[value]`-bound kendo control. */
  get viewItem(): SelectedItem | null {
    return typeaheadViewItem(this.selectedItem);
  }

  /** The control's ng-* status, mirrored onto a `[value]`-bound kendo control (PR 5721 review). */
  get viewStatus(): TypeaheadViewStatus {
    return typeaheadViewStatus(this.viewControl());
  }

  /** The form control's disabled state, for `[disabled]` on a kendo control bound by `[value]`. */
  get viewDisabled(): boolean {
    return !!this.viewControl()?.disabled;
  }

  /**
   * For `[value]`-bound templates: keep `selectedItem` in step with EVERY write to the form
   * control (show/hide clearing a field, a reset, a patch from another control), not only picks,
   * prefill and auto-fill, so the dropdown shows what the form holds, as `[formControlName]` did.
   * Re-derives through the shared `resetSelectedItems`. Call once from ngOnInit, after
   * super.ngOnInit(), so config and group are set.
   */
  protected bindViewToControl() {
    bindTypeaheadViewToControl(
      this.viewControl(),
      this.destroy$,
      () => this.resetSelectedItems(),
      () => this.cdr?.markForCheck()
    );
  }

  /** This control's form control, or undefined before config/group are set. */
  private viewControl() {
    return typeaheadViewControl(this.config, this.group);
  }
}
